import { useState, useRef, useCallback } from "react";

/**
 * Hook para gravação de áudio
 */
const useAudioRecorder = () => {
  const [isRecording, setIsRecording] = useState(false);
  const [recordedAudio, setRecordedAudio] = useState(null);
  const [audioBlob, setAudioBlob] = useState(null);
  const [recordingDuration, setRecordingDuration] = useState(0);
  const [error, setError] = useState(null);

  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const streamRef = useRef(null);
  const durationIntervalRef = useRef(null);

  /**
   * Iniciar gravação
   */
  const startRecording = useCallback(async () => {
    try {
      setError(null);

      // Solicitar acesso ao microfone
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          sampleRate: 16000,
        },
      });

      streamRef.current = stream;

      // Criar MediaRecorder
      const recorder = new MediaRecorder(stream, {
        mimeType: "audio/webm;codecs=opus",
      });

      audioChunksRef.current = [];

      // Coletar dados de áudio
      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      // Quando parar a gravação
      recorder.onstop = () => {
        const blob = new Blob(audioChunksRef.current, {
          type: "audio/webm;codecs=opus",
        });
        setAudioBlob(blob);

        // Criar URL para preview
        const audioUrl = URL.createObjectURL(blob);
        setRecordedAudio(audioUrl);

        // Parar todas as tracks do stream
        if (streamRef.current) {
          streamRef.current.getTracks().forEach((track) => track.stop());
          streamRef.current = null;
        }

        // Parar contador de duração
        if (durationIntervalRef.current) {
          clearInterval(durationIntervalRef.current);
          durationIntervalRef.current = null;
        }
      };

      mediaRecorderRef.current = recorder;
      recorder.start();
      setIsRecording(true);
      setRecordingDuration(0);

      // Iniciar contador de duração
      durationIntervalRef.current = setInterval(() => {
        setRecordingDuration((prev) => prev + 1);
      }, 1000);
    } catch (err) {
      console.error("Erro ao iniciar gravação:", err);
      setError("Não foi possível acessar o microfone");

      // Limpar
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
        streamRef.current = null;
      }
    }
  }, []);

  /**
   * Parar gravação e retornar o blob
   */
  const stopRecording = useCallback(() => {
    return new Promise((resolve) => {
      if (mediaRecorderRef.current && mediaRecorderRef.current.state === "recording") {
        // Override onstop para retornar o blob
        const originalOnStop = mediaRecorderRef.current.onstop;
        mediaRecorderRef.current.onstop = (event) => {
          const blob = new Blob(audioChunksRef.current, {
            type: "audio/webm;codecs=opus",
          });
          setAudioBlob(blob);

          // Criar URL para preview
          const audioUrl = URL.createObjectURL(blob);
          setRecordedAudio(audioUrl);

          // Parar todas as tracks do stream
          if (streamRef.current) {
            streamRef.current.getTracks().forEach((track) => track.stop());
            streamRef.current = null;
          }

          // Parar contador de duração
          if (durationIntervalRef.current) {
            clearInterval(durationIntervalRef.current);
            durationIntervalRef.current = null;
          }

          setIsRecording(false);
          resolve({ blob, audioUrl });
        };

        mediaRecorderRef.current.stop();
      } else {
        resolve(null);
      }
    });
  }, []);

  /**
   * Cancelar gravação (descartar)
   */
  const cancelRecording = useCallback(() => {
    // Parar gravação se estiver gravando
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === "recording") {
      mediaRecorderRef.current.stop();
    }

    // Parar stream
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }

    // Parar contador
    if (durationIntervalRef.current) {
      clearInterval(durationIntervalRef.current);
      durationIntervalRef.current = null;
    }

    // Limpar estado
    setIsRecording(false);
    setRecordedAudio(null);
    setAudioBlob(null);
    setRecordingDuration(0);
    audioChunksRef.current = [];
  }, []);

  /**
   * Limpar áudio gravado
   */
  const clearRecordedAudio = useCallback(() => {
    if (recordedAudio) {
      URL.revokeObjectURL(recordedAudio);
    }
    setRecordedAudio(null);
    setAudioBlob(null);
    setRecordingDuration(0);
  }, [recordedAudio]);

  /**
   * Formatar duração
   */
  const formatDuration = useCallback((seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  }, []);

  return {
    // State
    isRecording,
    recordedAudio,
    audioBlob,
    recordingDuration,
    recordingTime: recordingDuration, // Alias para compatibilidade
    formattedDuration: formatDuration(recordingDuration),
    error,
    hasRecording: !!recordedAudio,

    // Actions
    startRecording,
    stopRecording,
    cancelRecording,
    clearRecordedAudio,
  };
};

export default useAudioRecorder;
